import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { saveAs } from 'file-saver';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Button } from '@/components/Button';
import { Alert } from '@/components/Alert';
import { opportunityApi, opportunityErrors } from '@/pages/Opportunities/api';
import type { OpportunityDetail } from '@/pages/Opportunities/types';

export interface OpportunityDocumentsProps { item: OpportunityDetail; onSaved: (item: OpportunityDetail) => void; }
export const OpportunityDocuments = ({ item, onSaved }: OpportunityDocumentsProps) => {
    const { t } = useTranslation('global');
    const [file, setFile] = useState<File | null>(null);
    const [purpose, setPurpose] = useState('general');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const fileInput = useRef<HTMLInputElement>(null);
    const upload = async (): Promise<void> => {
        if(file === null){
            return;
        }
        setBusy(true); setError('');
        try{
            onSaved(await opportunityApi.upload(item.id, item.record_version, file, purpose));
            setFile(null);
            if(fileInput.current){
                fileInput.current.value = '';
            }
        }
        catch(failure){
            setError(Object.values(opportunityErrors(failure)).join(' '));
        }
        finally{
            setBusy(false);
        }
    };
    const download = async (id: number, name: string): Promise<void> => {
        try{
            saveAs(await opportunityApi.download(item.id, id), name);
        }
        catch(failure){
            setError(Object.values(opportunityErrors(failure)).join(' '));
        }
    };
    return <section className="space-y-4">
        {error && <Alert variant="error">{error}</Alert>}
        {!item.deleted_at && <div className="space-y-3">
            <Input ref={fileInput} label={t('opportunities.file')} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.bc3,.png,.jpg,.jpeg,.zip,.dwg,.dxf" onChange={event => setFile(event.target.files?.[0] ?? null)} />
            <Select label={t('opportunities.document_purpose')} value={purpose} onChange={setPurpose} options={['general', 'decision', 'bc3'].map(value => ({ value, label: t(`opportunities.document_type.${value}`) }))} />
            <Button icon="upload" onClick={() => { void upload(); }} disabled={!file || busy} loading={busy}>{t('opportunities.upload')}</Button>
            <p className="text-xs text-slate-600 dark:text-zinc-300">{t('opportunities.document_retention')}</p>
        </div>}
        {item.documents.length === 0 && <p>{t('opportunities.no_documents')}</p>}
        {item.documents.map(document => <div key={document.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 p-3 dark:border-white/15">
            <span className="break-all text-sm">{document.name}</span><Button variant="outline" icon="download" onClick={() => { void download(document.id, document.name); }}>{t('opportunities.download')}</Button>
        </div>)}
    </section>;
};
